/**
 * Preview mode — the preventer layer, bypassed.
 *
 * A beta preview of this app cannot run with the preventer enabled. The script does not
 * merely withhold a token when a display's fingerprint is unregistered: it **navigates the
 * page away** to `preventer-check.html` (docs/MIGRATION.md §6). Every visitor to a preview
 * URL would land on the preventer's registration page instead of the signage, so there
 * would be nothing to preview.
 *
 * With `VITE_PREVIEW_MODE=true` the app:
 *
 * - does not load `preventer.js` at all (the tag is stripped from `index.html` at build
 *   time by the plugin in `vite.config.ts`), so nothing can redirect the page;
 * - never acquires a token, never calls `savemeta`, and never opens an MQTT connection;
 * - renders every widget in the empty state it shows before its first message — which is
 *   documented per widget in docs/MIGRATION-WIDGETS.md §9;
 * - shows a fixed on-screen badge, so a screenshot of a preview build can never be mistaken
 *   for a working display.
 *
 * That makes it useful for exactly one thing: reviewing layout, theming, typography, the
 * root font-size scale and per-client configs. It proves nothing about data.
 *
 * ## Why a build-time constant and not a runtime setting
 *
 * `import.meta.env.VITE_PREVIEW_MODE` is substituted with a literal during the build, so
 * this constant folds and the branch in `MqttProvider` is decided at compile time rather
 * than on every render.
 *
 * The main reason it is build-time, though, is that preview mode then **cannot** be
 * switched on by an environment variable at `docker run` time on a production display.
 * Turning it on requires deliberately building a different image.
 *
 * ## What a preview bundle actually contains — measured
 *
 * Checked against a real preview build (682 kB) produced from a fully populated `.env`:
 *
 * | | in a preview bundle |
 * | --- | --- |
 * | `VITE_PREVENTER_API` | absent |
 * | savemeta URL | absent |
 * | `tokenAcquiredCallback` | absent |
 * | `VITE_MQTT_URL` | **present** |
 * | mqtt.js itself | **present** |
 *
 * So `preventer.ts` is tree-shaken out completely while `client.ts` is not. The asymmetry
 * is not arbitrary: `preventer.ts` is a pure module, so Rollup can drop it once nothing
 * references its exports, whereas `client.ts` imports `mqtt`, whose package is not marked
 * side-effect-free — the import chain survives even though `connectSignageMqtt` is
 * unreachable. Exporting `LiveMqttProvider` so tests can target it explicitly makes that
 * more likely still.
 *
 * The practical reading: **a preview image does not carry the preventer API key**, by two
 * independent mechanisms — the module is shaken out, and the Dockerfile does not supply the
 * secret to a preview build anyway. It does carry the broker URL and the MQTT client, which
 * it never reaches. The broker URL is a hostname, not a credential.
 *
 * This is a measurement, not a guarantee: a dependency bump could change what Rollup keeps.
 * If the key's absence ever becomes something a deployment relies on rather than merely
 * benefits from, assert it in a build-time check the way `scripts/check-styles.ts` asserts
 * the stylesheet, instead of trusting this comment.
 */
export const PREVIEW_MODE = import.meta.env.VITE_PREVIEW_MODE === 'true';

if (PREVIEW_MODE) {
  console.warn(
    '%c[signage] PREVIEW MODE — preventer bypassed, MQTT disabled',
    'background:#b45309;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px',
    '\nWidgets render their no-data state. Nothing here says anything about live data ' +
      'correctness. Do not deploy this build to a display.',
  );
}
